// React
import React, { useEffect, useState } from 'react';

// Common
import Configs from '../Configs';
import * as apiCalls from '../common/apiCalls';
import {
  evaluatePasswordPolicy,
  meetsPasswordPolicy,
  PASSWORD_POLICY_ERROR_KEYS,
  PASSWORD_POLICY_RULE_IDS,
} from '../utils/passwordPolicy';

// 3P
import { useTranslation } from 'react-i18next';
import CryptoJS from 'crypto-js';
import hmacSHA512 from 'crypto-js/hmac-sha512';

// import Footer from '../common/footer';

// ---------------------------------------------------------------------------------------------------
function ResetPassword() {
  const [t, i18n] = useTranslation();

  // Variables set up
  const [passwordRepeat, setPasswordRepeat] = useState('');
  const [password, setPassword] = useState('');

  const [wrongPassword, setWrongPassword] = useState(false);
  const [missingField, setMissingField] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const [passwordErrors, setPasswordErrors] = useState(() =>
    PASSWORD_POLICY_RULE_IDS.map((error) => ({
      error,
      message: t(PASSWORD_POLICY_ERROR_KEYS[error]),
      status: false,
    }))
  );

  // Would we use this?
  const [token, setToken] = useState('');

  // ---------------------------------------------------------------------------------------------------
  // Data validation methods
  // ---------------------------------------------------------------------------------------------------
  useEffect(() => {
    // Extract token from URL params
    const windowUrl = window.location.search;
    const rpt = new URLSearchParams(windowUrl).get('rpt');

    // Set Token to be sent
    setToken(rpt);
    if (!rpt) {
      setSubmitError('password.reset.linkInvalid');
    }
    if (Configs.devEnvironment)
      console.log('[resetPassword.js][useEffect()] DEV ONLY log: -- rpt : ', rpt);
  }, []);

  // ---------------------------------------------------------------------------------------------------
  // Data validation methods
  // ---------------------------------------------------------------------------------------------------
  // Login button was pressed - validate the input
  async function validateInput(event) {
    event.preventDefault();
    setSubmitError(null);

    // Validate input
    if (passwordRepeat === password && password != '' && passwordRepeat != '') {
      const errors = validatePassword(password);
      setPasswordErrors(errors);
      setWrongPassword(false);
      setMissingField(false);

      if (!meetsPasswordPolicy(password)) {
        return;
      }
      submitNewPassword();
    } else if (password === '' && passwordRepeat === '') {
      setWrongPassword(false);
      setMissingField(true);
    } else {
      setMissingField(false);
      setWrongPassword(true);
    }
  }

  const validatePassword = (candidate) => {
    const checks = evaluatePasswordPolicy(candidate);
    return PASSWORD_POLICY_RULE_IDS.map((error) => ({
      error,
      message: t(PASSWORD_POLICY_ERROR_KEYS[error]),
      // `status: true` means the rule failed (legacy reset-password UI).
      status: !checks[error],
    }));
  };

  async function passwordUpdated(password) {
    // Validate password
    const errors = validatePassword(password);
    setPasswordErrors(errors);

    if (Configs.devEnvironment)
      console.log('[resetPassword.js][passwordUpdated()] - passwordErrors = ', errors);

    setPassword(password);
  }

  // ---------------------------------------------------------------------------------------------------
  // API calls
  // ---------------------------------------------------------------------------------------------------
  // Password update
  async function submitNewPassword() {
    // Fake log for password
    // if (Configs.devEnvironment) console.log("[resetPassword.js][useEffect()] - password = ", hmacSHA512("password", Configs.privateKey).toString());

    try {
      const rpt = new URLSearchParams(window.location.search).get('rpt') || token;
      const passwordUpdateBody = {
        token: rpt,
        password: hmacSHA512(password, Configs.privateKey).toString(),
        timestamp: new Date().toISOString(),
      };

      if (!rpt) {
        setSubmitError('password.reset.linkInvalid');
        return;
      }

      const result = await apiCalls.updatePassword(
        null,
        null,
        import.meta.env.VITE_UNAUTHENTICATED_REQUESTS_API_TOKEN,
        passwordUpdateBody
      );

      if (result === 401 || result === 400) {
        setSubmitError('password.reset.linkInvalid');
        return;
      }
      if (result === 429 || result?.status === 429) {
        setSubmitError('auth.rate-limit.reset');
        return;
      }
      if (typeof result === 'number' && result > 200) {
        setSubmitError('password.reset.error');
        return;
      }

      window.location.href = '../';
    } catch (error) {
      console.error('[resetPassword.js][submitNewPassword()] Error updating password: ', error);
      setSubmitError('password.reset.error');
    }
  }

  // ---------------------------------------------------------------------------------------------------
  // Render methods
  // ---------------------------------------------------------------------------------------------------
  return (
    <React.Fragment>
      <div className="flex flex-col justify-center h-screen bg-gradient-to-b from-green-200 to-white">
        <div className="flex flex-col justify-center border bg-white border-gray-200 shadow m-2 sm:m-20 p-20 rounded">
          <div className="flex flex-col md:flex-row justify-center items-center">
            <div className="flex container w-[100px] h-[100px] md:w-[150px] md:h-[150px]">
              <img
                className=" object-center object-contain"
                alt="Kincode-App-Logo"
                src={Configs.companyLogoLarge}
              />
            </div>
          </div>

          <div className="flex justify-center">
            {submitError || wrongPassword || missingField ? (
              <p
                className="mb-4 text-center text-sm text-red-600"
                role="alert"
                data-testid="reset-password-error"
              >
                {submitError
                  ? t(submitError)
                  : wrongPassword
                    ? t('password.reset.incorrect')
                    : t('password.reset.missing')}
              </p>
            ) : null}
          </div>

          <form
            onSubmit={validateInput}
            className="flex flex-col justify-center text-left items-center w-full"
          >
            <div className="text-lg m-4">{t('password.reset.explanation')}</div>

            <div className="flex flex-col justify-center items-start ">
              <div className="flex flex-col sm:flex-row sm:justify-between m-2 p-2">
                <label className="flex p-2"> 🔑 {t('password')}: </label>
                <input
                  type="password"
                  placeholder="****"
                  className="input-field"
                  autoComplete="off"
                  onChange={(e) => passwordUpdated(e.target.value)}
                ></input>
              </div>

              <div className="flex flex-col sm:flex-row sm:justify-between m-2 p-2">
                <label className="flex p-2"> 🔑 {t('password.repeat')}: </label>
                <input
                  type="password"
                  placeholder="****"
                  className="input-field"
                  autoComplete="off"
                  onChange={(e) => setPasswordRepeat(e.target.value)}
                ></input>
              </div>
            </div>

            <div className="save-button w-full lg:w-1/6 m-2 mt-4">
              <button type="submit" className="text-center w-full">
                {' '}
                {t('submit')}
              </button>
            </div>
          </form>

          {/* Password requirements  */}
          <div className="flex flex-col justify-between text-left items-center w-full text-xs mt-8">
            <div className="flex flex-col justify-center items-start">
              {passwordErrors?.length > 0 && password !== ''
                ? passwordErrors?.map((error, index) => (
                    <div key={index} className="flex flex-row justify-center items-center">
                      <div className="flex p-2">
                        {error?.status === true ? '❌ ' : '✅ '} {error.message}
                      </div>
                    </div>
                  ))
                : null}
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

export default ResetPassword;
